import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    FaChevronLeft,
    FaLock,
    FaChevronDown,
    FaChevronUp,
    FaPlay,
    FaCheckCircle
} from 'react-icons/fa';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

const IndividualMockTestsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { subject, subjectDisplayName, testType } = location.state || {};

    const [studentId, setStudentId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [individualTests, setIndividualTests] = useState([]);
    const [expandedTests, setExpandedTests] = useState({});
    const [testProgress, setTestProgress] = useState({});
    const [subjectAccess, setSubjectAccess] = useState(false);

    // Subject configuration based on the selected subject
    const getSubjectConfig = () => {
        const configs = {
            Maths: {
                displayName: 'Mathematical Reasoning',
                fullName: 'Mathematical Reasoning Mock Test',
                route: '/mock-test/maths',
                color: 'from-blue-500 to-teal-400',
                bgColor: 'bg-blue-50',
                textColor: 'text-blue-700'
            },
            ThinkingSkills: {
                displayName: 'Thinking Skills',
                fullName: 'Thinking Skills Mock Test',
                route: '/mock-test/thinkingskills',
                color: 'from-purple-500 to-pink-400',
                bgColor: 'bg-purple-50',
                textColor: 'text-purple-700'
            },
            Writing: {
                displayName: 'Writing',
                fullName: 'Writing Mock Test',
                route: '/writing-test',
                color: 'from-orange-500 to-yellow-400',
                bgColor: 'bg-orange-50',
                textColor: 'text-orange-700'
            },
            Reading: {
                displayName: 'Reading',
                fullName: 'Reading Mock Test',
                route: '/mock-test/reading',
                color: 'from-teal-500 to-green-400',
                bgColor: 'bg-teal-50',
                textColor: 'text-teal-700'
            }
        };
        return configs[subject] || configs.Maths;
    };

    const subjectConfig = getSubjectConfig();

    useEffect(() => {
        if (subject) {
            // If subject is lowercase, capitalize it properly
            const properSubjectMap = {
                'maths': 'Maths',
                'thinkingskills': 'ThinkingSkills',
                'writing': 'Writing',
                'reading': 'Reading'
            };
            
            // Check if subject is lowercase and map it to proper format
            if (subject.toLowerCase() !== subject) {
                // Already in proper format, keep as is
            } else {
                // Convert lowercase to proper format
                subject = properSubjectMap[subject.toLowerCase()] || 'Maths';
            }
        }
    }, [subject]);

    useEffect(() => {
        const fetchData = async () => {
            if (!subject) {
                console.error("No subject provided in location state");
                navigate('/new-student-dashboard');
                return;
            }
            const userData = localStorage.getItem("user");
            if (userData) {
                const user = JSON.parse(userData);
                setStudentId(user.id);

                try {
                    // Check individual subject access
                    const accessResponse = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/check-individual-access/${user.id}/${subject}`
                    );
                    const accessData = await accessResponse.json();
                    setSubjectAccess(accessData.hasAccess);

                    if (accessData.hasAccess) {
                        // Generate 10 individual mock tests
                        const tests = [];
                        for (let i = 1; i <= 10; i++) {
                            tests.push({
                                id: `individual-${subject.toLowerCase()}-${i}`,
                                testNumber: i,
                                subject: subject,
                                testName: `${subjectConfig.displayName} Mock Test ${i}`,
                                isAvailable: i === 1, // First test always available
                                isCompleted: false
                            });
                        }
                        setIndividualTests(tests);

                        // Fetch test history to determine progress
                        const historyResponse = await fetch(
                            `${process.env.REACT_APP_BACKEND_URL}/api/auth/student/test-history/${user.id}`
                        );
                        if (historyResponse.ok) {
                            const historyData = await historyResponse.json();

                            console.log('Full history data:', historyData); // Debug log

                            // Filter individual mock tests for this subject only
                            const individualSubjectTests = historyData.filter(test => {
                                console.log('Checking test:', test); // Debug log
                                return test.testType === 'mock' &&
                                    test.test_category === 'individual-mock' &&
                                    test.subject?.toLowerCase() === subject.toLowerCase();
                            });

                            console.log('Filtered individual subject tests:', individualSubjectTests); // Debug log

                            // Calculate progress for the 10 tests
                            const progress = {};
                            const completedTestNumbers = new Set();

                            // Track completed tests using test_number
                            for (const test of individualSubjectTests) {
                                if (test.test_number) {
                                    completedTestNumbers.add(test.test_number);
                                    console.log(`Test ${test.test_number} completed for ${test.subject}`);
                                } else {
                                    // Fallback to extracting from testId if test_number not available
                                    if (test.testId && typeof test.testId === 'string') {
                                        const match = test.testId.match(/individual-(?:maths|thinkingskills|writing|reading)-(\d+)/i);
                                        if (match) {
                                            const extractedNumber = parseInt(match[1]);
                                            completedTestNumbers.add(extractedNumber);
                                            console.log(`Extracted test number ${extractedNumber} from testId`);
                                        }
                                    }
                                }
                            }

                            console.log('Completed test numbers:', Array.from(completedTestNumbers));

                            // Set progress for each test
                            for (let i = 1; i <= 10; i++) {
                                progress[i] = {
                                    isCompleted: completedTestNumbers.has(i),
                                    testName: `${subjectConfig.displayName} Mock Test ${i}`
                                };
                            }

                            setTestProgress(progress);

                            // Update tests availability based on completion
                            const updatedTests = tests.map(test => {
                                // Test is available if:
                                // 1. It's test number 1, OR
                                // 2. The previous test is completed
                                let isAvailable = false;

                                if (test.testNumber === 1) {
                                    isAvailable = true; // Test 1 always available
                                } else {
                                    // Check if the previous test is completed
                                    isAvailable = completedTestNumbers.has(test.testNumber - 1);
                                }

                                return {
                                    ...test,
                                    isAvailable: isAvailable,
                                    isCompleted: completedTestNumbers.has(test.testNumber)
                                };
                            });

                            setIndividualTests(updatedTests);

                            console.log('Updated tests availability:', updatedTests.map(t => ({
                                test: t.testNumber,
                                available: t.isAvailable,
                                completed: t.isCompleted
                            })));
                        } else {
                            console.error('Failed to fetch test history');
                        }
                    }
                } catch (error) {
                    console.error("Error fetching data:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        if (subject) {
            fetchData();
        } else {
            navigate('/new-student-dashboard');
        }
    }, [subject, navigate]);

    const toggleTestExpansion = (testId) => {
        setExpandedTests(prev => ({
            ...prev,
            [testId]: !prev[testId]
        }));
    };

    const handleStartTest = (test) => {
        if (!subjectAccess) {
            alert("You don't have access to this test. Please purchase the individual subject.");
            return;
        }

        // Navigate to instructions page for individual test with test number
        navigate('/individual-mock-test-instructions', {
            state: {
                subject: subject,
                testName: test.testName,
                testNumber: test.testNumber,
                testId: test.id,
                duration: subject === 'Writing' ? '30 minutes' : '40 minutes',
                individualTest: true
            }
        });
    };

    const handleBack = () => {
        navigate('/new-student-dashboard');
    };

    const getScoreForTest = (testNumber) => {
        // You can fetch and display scores from test history if needed
        return null;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
            </div>
        );
    }

    if (!subjectAccess) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 py-12 px-4">
                <div className="max-w-4xl mx-auto">
                    <button
                        onClick={handleBack}
                        className="flex items-center text-blue-600 hover:text-blue-800 transition-colors mb-6"
                    >
                        <FaChevronLeft className="mr-2" /> Back to Dashboard
                    </button>

                    <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                        <div className="inline-block p-6 rounded-full bg-yellow-100 mb-6">
                            <FaLock className="text-4xl text-yellow-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">
                            {subjectConfig.fullName}
                        </h2>
                        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                            You don't have access to this individual subject test. Please purchase access to start practicing.
                        </p>
                        <button
                            onClick={() => navigate('/new-student-dashboard')}
                            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:opacity-90 transition-all"
                        >
                            Go Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 py-12 px-4 md:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header with Back Button */}
                <div className="mb-8">
                    <button
                        onClick={handleBack}
                        className="flex items-center text-blue-600 hover:text-blue-800 transition-colors mb-4"
                    >
                        <FaChevronLeft className="mr-2" /> Back to Dashboard
                    </button>

                    <div className="text-center">
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                            {subjectConfig.fullName}
                        </h1>
                        <p className="text-gray-600">
                            Complete all 10 tests to master {subjectConfig.displayName}
                        </p>
                    </div>
                </div>

                {/* Tests List */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="space-y-4">
                        {individualTests.map((test) => {
                            const isAvailable = test.isAvailable;
                            const isCompleted = test.isCompleted;

                            return (
                                <div
                                    key={test.id}
                                    className={`border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow ${!isAvailable ? 'opacity-60' : ''
                                        }`}
                                >
                                    {/* Test Header - Always visible */}
                                    <div
                                        className={`p-4 flex justify-between items-center cursor-pointer transition-colors ${isAvailable ? 'bg-gray-50 hover:bg-gray-100' : 'bg-gray-100'
                                            }`}
                                        onClick={() => isAvailable && toggleTestExpansion(test.id)}
                                    >
                                        <div className="flex items-center space-x-4">
                                            {/* Test Number Circle */}
                                            <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
                        ${isCompleted
                                                    ? 'bg-green-500 text-white'
                                                    : isAvailable
                                                        ? 'bg-blue-500 text-white'
                                                        : 'bg-gray-400 text-white'
                                                }
                      `}>
                                                {isCompleted ? '✓' : test.testNumber}
                                            </div>

                                            <div>
                                                <h4 className="font-bold text-gray-800 text-lg">
                                                    {test.testName}
                                                </h4>
                                                <p className="text-sm text-gray-600">
                                                    {isCompleted
                                                        ? 'Completed'
                                                        : isAvailable
                                                            ? 'Ready to start'
                                                            : 'Complete previous test to unlock'
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-3">
                                            {!isAvailable && <FaLock className="text-gray-400" />}
                                            {isAvailable && (
                                                expandedTests[test.id] ?
                                                    <FaChevronUp className="text-gray-400" /> :
                                                    <FaChevronDown className="text-gray-400" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded Content - Shows only when test is available */}
                                    {expandedTests[test.id] && isAvailable && (
                                        <div className="p-6 bg-white border-t border-gray-200">
                                            <div className="text-center">
                                                <div className="mb-6">
                                                    <h5 className="text-lg font-semibold text-gray-800 mb-2">
                                                        {test.testName}
                                                    </h5>
                                                    <p className="text-gray-600 mb-4">
                                                        This test contains questions to assess your {subjectConfig.displayName} skills.
                                                        {subject === 'Writing'
                                                            ? ' You will have 30 minutes to complete the writing task.'
                                                            : ' You will have 40 minutes to complete all questions.'
                                                        }
                                                    </p>

                                                    {/* Start Button - Single subject per test */}
                                                    <button
                                                        onClick={() => handleStartTest(test)}
                                                        className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
                                                    >
                                                        <FaPlay className="mr-2" size={14} />
                                                        Start Test
                                                    </button>
                                                </div>

                                                {/* Show completion message if test is done */}
                                                {isCompleted && (
                                                    <div className="mt-4 flex items-center justify-center text-green-600">
                                                        <FaCheckCircle className="mr-2" />
                                                        <span>You have completed this test</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndividualMockTestsPage;